import { invariant } from "@olenbetong/appframe-core";

import { getDefaultClient, type IClient } from "./Client.js";
import * as errors from "./errors.js";
import { EventTarget } from "./events.js";
import type { FieldDefinition } from "./fields.js";
import { UploadTask, type UploadUrlOptions } from "./upload/UploadTask.js";
import { fieldTypes, guessType } from "./utils/fieldTypes.js";
import { getFields, handleResponseError } from "./utils/index.js";

function getFieldType(field: FieldDefinition, value: any): string {
	let type = field.type ?? guessType(value);
	if (!field.type && value !== null) {
		field.type = type;
	}

	return type;
}

export function isRequestError(result: any): result is RequestError {
	return result !== null && !Array.isArray(result) && Object.hasOwn(result, "error");
}

export function arrayRecordToObject<T>(fields: FieldDefinition[], data: any[], dataSourceId?: string | null): T {
	dataSourceId = dataSourceId ?? "Anonymous data source";
	invariant(
		fields.length === data.length,
		`${dataSourceId}: Incorrect number of fields in record was passed to the 'from' parameter of parseData`,
	);

	let record: any = {};
	for (let i = 0; i < fields.length; i++) {
		let value = data[i];
		let field = fields[i];
		let type = getFieldType(field, value);
		let fieldType = fieldTypes[type];

		if (fieldType) {
			invariant(fieldType.validate(value), errors.incorrectTypeOfField(dataSourceId, field.name));
			const propName = (field.alias ?? field.aliasName ?? field.name) as keyof T;
			record[propName] = value === null ? null : fieldType.parse(value);
		}
	}

	return record as T;
}

export function arrayRecordsToObjects<T>(fields: FieldDefinition[], data: any[][], dataSourceId?: string | null): T[] {
	return data.map((record) => arrayRecordToObject(fields, record, dataSourceId ?? undefined));
}

/**
 * Normalized error object returned when a DataHandler request fails
 */
export type RequestError = {
	aborted?: boolean;
	error: string;
	internalError?: any;
	logged?: boolean;
	stack?: string;
	status?: number;
	statusText?: string;
	responseText?: string;
};

export enum RequestType {
	Create = "create",
	Destroy = "destroy",
	Retrieve = "retrieve",
	Update = "update",
	Rowcount = "rowcount",
	DistinctData = "distinctData",
	BulkDestroy = "bulkdestroy",
	BulkInsert = "bulkinsert",
}
export interface DataHandlerConstructor {
	new (options?: any): DataHandler<any>;
}

export type DataHandlerDescriptor = {
	options: any;
	type: DataHandlerConstructor | string | number;
};

export enum SortOrder {
	Asc = "asc",
	Desc = "desc",
	AscNullsLast = "ascnullslast",
	DescNullsFirst = "descnullsfirst",
}

/**
 * Filter object passed to whereObject or filterObject
 */
export type Filter = FilterGroup | FilterExpression | null;
export type FilterObject = Filter;

export type FilterGroup = {
	type: "group";
	name?: string;
	mode: "and" | "or";
	items: Array<FilterGroup | FilterExpression>;
};

export type FilterExpression = {
	type: "expression";
	column: string;
	operator: string;
	value: string | string[] | Date | number | null;
	valueType: string;
};

/**
 * Parameters passed to a retrieve, distinctData or rowcount request on a DataHandler
 */
export type RetrieveParameters<T> = {
	[index: string]: any;
	distinctRows?: boolean;
	fields?: FieldDefinition[];
	filterObject?: Filter | null;
	filterString?: string;
	groupBy?: string[];
	masterChildCriteria?: Partial<T>;
	maxRecords?: number;
	skip?: number;
	sortOrder?: Array<Record<string | number | symbol, SortOrder>>;
	uniqueName?: string;
	returnTotal?: boolean;
	whereClause?: string;
	whereObject?: Filter | null;
};

export type PagedRetrieveParameters<T> = RetrieveParameters<T> & {
	skip: number;
};

export type RequestOptions = Partial<{
	signal: AbortSignal;
}>;

export type RequestData<T> = Partial<PagedRetrieveParameters<T>>;
export type APIRequestData<T> = RequestData<T> & {
	operation: RequestType;
	resourceName: string;
	fields: FieldDefinition[];
	excludeFieldNames?: boolean;
};

/**
 * Parameters required by a bulkdestroy request on a DataHandler
 */
export type BulkDestroyParameters =
	| {
			FilterString?: string;
			Ids: string;
			Key: string;
			MasterChildCriteria: Record<string, any>;
			UpdateColumns: Record<string, any>;
			WhereClause?: string;
	  }
	| {
			PrimKeys: string[][];
	  };

export type BulkInsertParameters<T> = Partial<T> & {
	/**
	 * Field definitions of fields included in Data
	 */
	Fields: FieldDefinition[];
	/**
	 * Data values in same order as the Fields
	 */
	Data: any[][];
	Bulk: boolean;
	Json: boolean;
};

export type BulkUpdateParameters = {
	Fields: FieldDefinition[];
	Data: any[][];
};

/**
 * Parameters required by a distinctData request on a DataHandler
 */
export type DistinctDataRequestData = {
	parameters: {
		field: string;
		fields: string;
	};
};

/**
 * Parameters required by a destroy request on a DataHandler
 */
export type DestroyRequest = {
	PrimKey: string;
};

/**
 * Type returned from a rowcount request on a DataHandler
 */
export type PagedRetrieveResponse<T> = {
	data: T[];
	skip: number;
	total: number;
};

/**
 * Type returned from a retrieve request on a DataHandler
 */
export type RetrieveResponse<T> = T[] | PagedRetrieveResponse<T>;

export type DataProviderHandlerBaseOptions = {
	timeout?: number;
	client?: IClient | null;
};

export type DataProviderHandlerOptions = DataProviderHandlerBaseOptions & {
	articleId: string;
	dataSourceId: string | null;
	fields: Array<string | FieldDefinition>;
	groupBy?: string[];
};

export type DataProviderHandlerAPIOptions = Omit<DataProviderHandlerOptions, "articleId"> & {
	uniqueName?: string;
	fields: string | Array<string | FieldDefinition> | null;
	dataSourceId: string;
};

type DataProviderHandlerEvent = { onSuccess: unknown; onError: string; onComplete: unknown };

const DataProviderHandlerEvents = {
	onSuccess: { abortable: false },
	onError: { abortable: false },
	onComplete: { abortable: false }, // Fires after a request completes
};

export abstract class DataHandler<T extends Record<string, unknown>> extends EventTarget<DataProviderHandlerEvent> {
	abstract create(data: Partial<T>, options?: RequestOptions): Promise<T | null>;
	abstract destroy(data: Partial<T>, options?: RequestOptions): Promise<boolean>;
	abstract retrieve<V = T>(data: RetrieveParameters<V>, options?: RequestOptions): Promise<V[]>;
	abstract retrieve<V = T>(
		data: PagedRetrieveParameters<V>,
		options?: RequestOptions,
	): Promise<PagedRetrieveResponse<V>>;
	abstract rowcount(data: RequestData<T>, options?: RequestOptions): Promise<PagedRetrieveResponse<T>>;
	abstract update(data: Partial<T>, options?: RequestOptions): Promise<T | null>;
	abstract bulkdestroy(data: BulkDestroyParameters): Promise<boolean>;
	abstract bulkinsert(data: BulkInsertParameters<T>): Promise<T[]>;
	abstract distinctData(data: DistinctDataRequestData): Promise<T[]>;
	abstract getFileStoreURL(primKey: string, fileName: string, type?: "download" | "view"): string;
	abstract getBlobURL(primKey: string, field: string, fileName: string, type?: "download" | "view"): string;
	abstract getExportURL(format: "pdf" | "xlsx" | "csv" | "xls"): string;
	abstract getUploadUrl(options?: UploadUrlOptions): string | null;
	createUploadTask?(file: File | Blob, options?: UploadUrlOptions): UploadTask {
		return new UploadTask({ ...options, file, dataHandler: this });
	}

	constructor() {
		super(DataProviderHandlerEvents);
	}
}

function getDefaultOptions() {
	return {
		articleId: globalThis.af?.article?.id,
		dataSourceId: null,
		timeout: 0,
		fields: [],
		groupBy: false,
	};
}

abstract class DataProviderHandlerBase<T extends Record<string, unknown>> extends DataHandler<T> {
	protected previousRetrieveRequest: AbortController | null = null;
	private options: DataProviderHandlerBaseOptions;
	get client(): IClient {
		return this.options.client ?? getDefaultClient();
	}
	set client(value: IClient | null) {
		this.options.client = value;
	}

	constructor(options: DataProviderHandlerBaseOptions) {
		super();
		this.options = options;
	}

	async request<DataType = T>(
		url: string,
		data: RequestData<any> | null,
		options: RequestOptions = {},
	): Promise<DataType> {
		let requestInit: RequestInit & { timeout?: number } = {
			body: JSON.stringify(data),
			timeout: this.options.timeout,
			...options,
		};

		const response = await this.client.afFetch(url, requestInit);

		if (response.ok) {
			let data = await response.json();
			if (Object.hasOwn(data, "success")) {
				this.fireEvent("onSuccess", data.success as T);

				return data.success as DataType;
			}
		}

		const responseError = await handleResponseError(response);

		if (responseError.canRetry) {
			return await this.request(url, data, options);
		}

		this.fireEvent("onError", responseError.message);

		throw Error(responseError.message);
	}
}

export class DataProviderHandler<T extends Record<string, unknown>>
	extends DataProviderHandlerBase<T>
	implements DataHandler<T>
{
	private _options: DataProviderHandlerOptions;
	fields: FieldDefinition[];

	constructor(options: Pick<DataProviderHandlerOptions, "fields"> & Partial<DataProviderHandlerOptions>) {
		super(Object.assign({}, getDefaultOptions(), options || {}, 1));
		this._options = Object.assign({}, getDefaultOptions(), options || {}, 1);
		this.fields = this._options.fields.map((field: FieldDefinition | string) => {
			if (typeof field === "string") {
				return { name: field };
			}
			return field;
		});
	}

	private getFieldsUrlPart() {
		let fields = "";
		if (this._options.fields) {
			if (this._options.fields.length > 1) {
				fields = this._options.fields.toString().replace(/,/g, "-");
			}

			if (this._options.groupBy) {
				fields += `/${this._options.groupBy}`;
			}
		}

		return fields;
	}

	private getUrlForType(type: RequestType, includeFields = false) {
		return `/${type}/${this._options.articleId}/${this._options.dataSourceId}/${
			includeFields ? this.getFieldsUrlPart() : ""
		}`;
	}

	private getArticleId() {
		let articleId = this._options.articleId;
		if (/\.\d{3}$/.test(articleId)) {
			articleId = articleId.substring(0, articleId.length - 4);
		}
		return articleId;
	}

	getFileStoreURL(primKey: string, fileName: string, type: "download" | "view" = "view") {
		return `/file/${type}/${this.getArticleId()}/${this._options.dataSourceId}/${primKey}/${encodeURIComponent(
			fileName,
		)}`;
	}

	getBlobURL(primKey: string, field: string, fileName: string, _type: "download" | "view" = "view") {
		return `/file/db/${this.getArticleId()}/${this._options.dataSourceId}/${field}/${primKey}/${encodeURIComponent(
			fileName,
		)}`;
	}

	getExportURL(format: "pdf" | "xlsx" | "csv" | "xls"): string {
		return `/api/data-object/export/${this.getArticleId()}/${this._options.dataSourceId}/${format}`;
	}

	getUploadUrl(options: UploadUrlOptions = {}) {
		let url = `/file/upload/${this.getArticleId()}/${this._options.dataSourceId}`;

		if (options.fieldName) {
			url += `/${options.fieldName}`;
		}

		if (options.primKey) {
			url += `/${options.primKey}`;
		}

		return url;
	}

	async create(data: RequestData<T>, options?: RequestOptions): Promise<T | null> {
		const result = await this.request<T[keyof T][] | null>(
			`/create/${this._options.articleId}/${this._options.dataSourceId}`,
			data,
			options,
		);

		if (result === null) return null;

		return arrayRecordToObject(data.fields ?? this.fields, result, this._options.dataSourceId ?? "");
	}

	async retrieve<V = T>(data: RetrieveParameters<V>, options?: RequestOptions): Promise<V[]>;
	async retrieve<V = T>(data: PagedRetrieveParameters<V>, options?: RequestOptions): Promise<PagedRetrieveResponse<V>>;
	async retrieve<V = T>(data: RetrieveParameters<V> | PagedRetrieveParameters<V>, options?: RequestOptions) {
		const result = await this.request<RetrieveResponse<T[keyof T][]>>(
			this.getUrlForType(RequestType.Retrieve),
			data,
			options,
		);
		if (Array.isArray(result)) {
			return arrayRecordsToObjects<T>(data.fields ?? this.fields, result, this._options.dataSourceId);
		} else {
			(result as any).data = arrayRecordsToObjects<T>(data.fields ?? this.fields, result.data);
			return result as unknown as RetrieveResponse<T>;
		}
	}

	async rowcount<RecordType = T>(data: RequestData<RecordType>, options?: RequestOptions) {
		return this.request<PagedRetrieveResponse<RecordType>>(this.getUrlForType(RequestType.Rowcount), data, options);
	}

	async update(data: RequestData<T>, options?: RequestOptions): Promise<T | null> {
		let response = await this.request<T[keyof T][] | null>(this.getUrlForType(RequestType.Update), data, options);

		return response ? arrayRecordToObject(data.fields ?? this.fields, response, this._options.dataSourceId) : response;
	}

	async destroy(data: RequestData<T>, options?: RequestOptions) {
		return this.request<boolean>(this.getUrlForType(RequestType.Destroy), data, options);
	}

	async bulkdestroy(data: BulkDestroyParameters, options?: RequestOptions) {
		let result = await this.request<boolean>(this.getUrlForType(RequestType.BulkDestroy, false), data, options);

		return result;
	}

	private resultSetToObjects<T>(result: T[keyof T][][], fields?: FieldDefinition[]): T[] {
		return arrayRecordsToObjects(fields ?? this.fields, result, this._options.dataSourceId);
	}

	async bulkinsert(data: RequestData<T>, options?: RequestOptions) {
		let result = await this.request<T[keyof T][][]>(this.getUrlForType(RequestType.BulkInsert), data, options);
		let parsed = this.resultSetToObjects(result);

		return parsed;
	}

	async distinctData(data: DistinctDataRequestData, options?: RequestOptions) {
		let result = await this.request<T[keyof T][][]>(
			`/retrieve/${this._options.articleId}/${this._options.dataSourceId}/${data.parameters.fields}/true/${data.parameters.field}`,
			data,
			options,
		);
		let parsed = this.resultSetToObjects(result);

		return parsed;
	}
}

export class DataProviderHandlerAPI<T extends Record<string, unknown>>
	extends DataProviderHandlerBase<T>
	implements DataHandler<T>
{
	private _options: DataProviderHandlerAPIOptions;
	fields: FieldDefinition[];

	constructor(options: DataProviderHandlerAPIOptions) {
		super(Object.assign({}, getDefaultOptions(), options));
		this._options = options;
		this.fields = this._options.fields.map((field: FieldDefinition | string) => {
			if (typeof field === "string") {
				return { name: field };
			}
			return field;
		});
	}

	private resultSetToObjects<T>(result: T[keyof T][][], fields?: FieldDefinition[]): T[] {
		return arrayRecordsToObjects(fields ?? this.fields, result, this._options.dataSourceId);
	}

	private resultRowToObject(result: T[keyof T][], fields?: FieldDefinition[]): T {
		return arrayRecordToObject(fields ?? this.fields, result, this._options.dataSourceId);
	}

	private getParameters(type: RequestType, data: RequestData<T>): APIRequestData<T> {
		let params: APIRequestData<T> = {
			operation: type,
			resourceName: this._options.dataSourceId,
			fields: getFields(this._options.fields),
			excludeFieldNames: true,
			...data,
		};

		if (type === RequestType.Retrieve && this._options.groupBy) {
			params.groupBy = this._options.groupBy;
		}

		if (this._options.uniqueName) {
			params.uniqueName = this._options.uniqueName;
		}

		return params;
	}

	getFileStoreURL(primKey: string, fileName: string, type: "download" | "view" = "view") {
		return `/api/data/file/${type}/${this._options.dataSourceId}/${primKey}/${encodeURIComponent(fileName)}`;
	}

	getBlobURL(primKey: string, field: string, fileName: string, _type: "download" | "view" = "view") {
		return `/api/data/blob/${this._options.dataSourceId}/${primKey}/${field}/${encodeURIComponent(fileName)}`;
	}

	getExportURL(format: "pdf" | "xlsx" | "csv" | "xls"): string {
		return `/api/data/export/${this._options.dataSourceId}/${format}`;
	}

	getUploadUrl(options: UploadUrlOptions = {}) {
		let url: string;
		if (options.fieldName) {
			url = `/api/data/blob/upload/${this._options.dataSourceId}/${options.fieldName}`;
		} else {
			url = `/api/data/file/upload/${this._options.dataSourceId}`;
		}

		if (options.primKey) {
			url += `/${options.primKey}`;
		}

		return url;
	}

	async create(data: RequestData<T>, options?: RequestOptions): Promise<T | null> {
		let result = await this.request<T[keyof T][] | null>(
			"/api/data",
			this.getParameters(RequestType.Create, data),
			options,
		);
		return result == null ? null : this.resultRowToObject(result, data.fields);
	}

	async retrieve<V = T>(data: RetrieveParameters<V>, options?: RequestOptions): Promise<V[]>;
	async retrieve<V = T>(data: PagedRetrieveParameters<V>, options?: RequestOptions): Promise<PagedRetrieveResponse<V>>;
	async retrieve(data: RequestData<T>, options?: RequestOptions) {
		let result = await this.request<RetrieveResponse<T[keyof T][][]>>(
			"/api/data",
			this.getParameters(RequestType.Retrieve, data),
			options,
		);
		let parsed: RetrieveResponse<T>;
		if (Array.isArray(result)) {
			parsed = arrayRecordsToObjects(data.fields ?? this.fields, result, this._options.dataSourceId);
		} else {
			parsed = {
				...result,
				data: arrayRecordsToObjects(data.fields ?? this.fields, result.data, this._options.dataSourceId),
			};
		}
		return parsed;
	}

	async rowcount(data: RequestData<T>, options?: RequestOptions) {
		let result = await this.request<PagedRetrieveResponse<T>>(
			"/api/data",
			this.getParameters(RequestType.Rowcount, data),
			options,
		);

		return result;
	}

	async update(data: RequestData<T>, options?: RequestOptions): Promise<T | null> {
		let result = await this.request<T[keyof T][] | null>(
			"/api/data",
			this.getParameters(RequestType.Update, data),
			options,
		);
		let parsed = !result ? result : this.resultRowToObject(result, data.fields);

		return parsed;
	}

	async destroy(data: RequestData<T>, options?: RequestOptions) {
		let result = await this.request<boolean>("/api/data", this.getParameters(RequestType.Destroy, data), options);
		return result;
	}

	async bulkdestroy(data: BulkDestroyParameters, options?: RequestOptions) {
		let result = await this.request<boolean>("/api/data", this.getParameters(RequestType.BulkDestroy, data), options);

		return result;
	}

	async bulkinsert(data: RequestData<T>, options?: RequestOptions) {
		let result = await this.request<T[keyof T][][]>(
			"/api/data",
			this.getParameters(RequestType.BulkInsert, data),
			options,
		);
		let parsed = this.resultSetToObjects(result, data.fields);
		return parsed;
	}

	async distinctData(data: DistinctDataRequestData, options?: RequestOptions) {
		let parameters = this.getParameters(RequestType.DistinctData, data);
		parameters.distinctRows = true;
		let result = await this.request<T[keyof T][][]>("/api/data", parameters, options);
		let parsed = this.resultSetToObjects(result);

		return parsed;
	}
}
